import React, { useRef, useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { resourceAPI } from '../../services/api';

/* ─────────────────────────────────────────────────────────────────────────────
   STYLES
───────────────────────────────────────────────────────────────────────────── */
const STYLES = `
  @keyframes lu-line-up {
    from { transform: translateY(108%); opacity: 0; }
    to   { transform: translateY(0);    opacity: 1; }
  }
  .lu-mask { overflow: hidden; display: block; }
  .lu-line {
    display: block;
    animation: lu-line-up 0.78s cubic-bezier(0.16, 1, 0.3, 1) both;
  }

  @keyframes lu-fade-up {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .lu-fade { animation: lu-fade-up 0.65s cubic-bezier(0.16, 1, 0.3, 1) both; }

  /* orbit draw-on */
  @keyframes lu-draw {
    from { stroke-dashoffset: var(--d, 1000); }
    to   { stroke-dashoffset: 0; }
  }
  .d {
    stroke-dasharray: var(--d, 1000);
    stroke-dashoffset: var(--d, 1000);
    animation: lu-draw 1.6s cubic-bezier(0.4, 0, 0.15, 1) both;
  }

  @keyframes lu-pop {
    from { opacity: 0; transform: scale(0.35) rotate(-12deg); }
    65%  { transform: scale(1.12) rotate(4deg); }
    to   { opacity: 1; transform: scale(1) rotate(0deg); }
  }
  .p { animation: lu-pop 0.48s cubic-bezier(0.34, 1.56, 0.64, 1) both; }

  @keyframes lu-fa { 0%,100%{transform:translateY(0)}   50%{transform:translateY(-10px)} }
  @keyframes lu-fb { 0%,100%{transform:translateY(0)}   50%{transform:translateY(8px)}   }
  @keyframes lu-fc { 0%,100%{transform:translateY(0) rotate(0deg)} 50%{transform:translateY(-7px) rotate(6deg)} }
  .fa { animation: lu-fa 4.5s ease-in-out infinite; }
  .fb { animation: lu-fb 5.8s ease-in-out 1s   infinite; }
  .fc { animation: lu-fc 6.2s ease-in-out 0.5s infinite; }

  @keyframes lu-spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
  .orbit-spin {
    animation: lu-spin 34s linear infinite;
    transform-origin: 160px 160px;
  }

  /* SVG avatar path paint-in */
  @keyframes avatar-paint {
    from { opacity: 0; }
    to   { opacity: 1; }
  }

  @keyframes lu-blob-a {
    0%,100%{ transform:translate(0,0)    scale(1);    }
    35%    { transform:translate(38px,-28px) scale(1.07); }
    68%    { transform:translate(-20px,32px) scale(0.94); }
  }
  @keyframes lu-blob-b {
    0%,100%{ transform:translate(0,0)    scale(1);    }
    40%    { transform:translate(-32px,20px) scale(0.92); }
    72%    { transform:translate(26px,-38px) scale(1.08); }
  }
`;

/* ─────────────────────────────────────────────────────────────────────────────
   PARTICLE PORTRAIT
   Reads real pixel data from daniel.png, then animates each pixel-block
   flying in from outside the circle to its correct position.
   Centre pixels (face) arrive first → portrait becomes recognisable early.
───────────────────────────────────────────────────────────────────────────── */
const ParticlePortrait = () => {
  const canvasRef = useRef(null);
  const rafRef    = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const SIZE = 200;
    const DPR  = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width  = SIZE * DPR;
    canvas.height = SIZE * DPR;
    const ctx = canvas.getContext('2d');
    ctx.scale(DPR, DPR);

    const STEP = 4;          // pixel-block size → ~1 900 particles inside circle
    const CX   = SIZE / 2;
    const CY   = SIZE / 2;
    const R    = SIZE / 2;

    const img = new Image();
    img.src = '/daniel.png';

    img.onload = () => {
      // ── Sample pixel colours from image ────────────────────────────
      const off = document.createElement('canvas');
      off.width = off.height = SIZE;
      off.getContext('2d').drawImage(img, 0, 0, SIZE, SIZE);
      const { data } = off.getContext('2d').getImageData(0, 0, SIZE, SIZE);

      // ── Build particle list ─────────────────────────────────────────
      const particles = [];

      for (let py = 0; py < SIZE; py += STEP) {
        for (let px = 0; px < SIZE; px += STEP) {
          const dx = px - CX, dy = py - CY;
          if (dx * dx + dy * dy > R * R) continue;   // outside circle → skip

          const i = (py * SIZE + px) * 4;
          if (data[i + 3] < 20) continue;             // transparent → skip

          // Start position: scattered from a ring outside the avatar
          // angle is roughly away from centre (so particles fly straight inward)
          const angle  = Math.atan2(dy, dx) + (Math.random() - 0.5) * 2.2;
          const dist   = R * 2.4 + Math.random() * R * 1.5;

          // Delay: centre arrives first so the face is recognisable early
          const normalised = Math.sqrt(dx * dx + dy * dy) / R;
          const delay = normalised * 380 + Math.random() * 160;

          particles.push({
            tx: px, ty: py,
            sx: CX + Math.cos(angle) * dist,
            sy: CY + Math.sin(angle) * dist,
            r: data[i], g: data[i + 1], b: data[i + 2],
            delay,
          });
        }
      }

      // ── Animation loop ──────────────────────────────────────────────
      const DURATION   = 1500;   // ms each particle travels
      const INIT_DELAY = 350;    // ms before anything moves

      let t0       = null;
      let fadingIn = false;
      let fadeT0   = 0;

      const draw = (now) => {
        if (!t0) t0 = now;
        const elapsed = now - t0 - INIT_DELAY;

        ctx.clearRect(0, 0, SIZE, SIZE);

        // Circular clip
        ctx.save();
        ctx.beginPath();
        ctx.arc(CX, CY, R - 0.5, 0, Math.PI * 2);
        ctx.clip();

        // Draw every particle at its current interpolated position
        let allDone = true;
        for (const p of particles) {
          const raw  = Math.max(0, elapsed - p.delay) / DURATION;
          const clamped = Math.min(raw, 1);
          if (clamped < 1) allDone = false;

          // ease-out quart: fast start, smooth landing
          const e = 1 - Math.pow(1 - clamped, 4);

          const x = p.sx + (p.tx - p.sx) * e;
          const y = p.sy + (p.ty - p.sy) * e;

          // Particle shrinks from STEP → 1px as it lands → gradual sharpening
          const ps = STEP * (1 - e) + 1 * e;

          ctx.fillStyle = `rgb(${p.r},${p.g},${p.b})`;
          ctx.fillRect(x - ps / 2, y - ps / 2, ps, ps);
        }

        // Once all particles landed, crossfade to the clean photo
        if (allDone && !fadingIn) { fadingIn = true; fadeT0 = now; }

        if (fadingIn) {
          const ft = Math.min((now - fadeT0) / 800, 1);
          ctx.globalAlpha = ft;
          ctx.drawImage(img, 0, 0, SIZE, SIZE);
          ctx.globalAlpha = 1;
          ctx.restore();
          if (ft < 1) rafRef.current = requestAnimationFrame(draw);
          // ft === 1 → animation fully done, stop RAF
          return;
        }

        ctx.restore();
        rafRef.current = requestAnimationFrame(draw);
      };

      rafRef.current = requestAnimationFrame(draw);
    };

    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="rounded-full shadow-2xl block"
      style={{ width: 200, height: 200 }}
    />
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   ANIMATED SVG AVATAR
   Fetches avatar.svg, adds a staggered opacity animation to each of the
   555 fill paths (largest areas first → face details last), creating a
   genuine "painted from scratch" reveal over ~2 seconds.
───────────────────────────────────────────────────────────────────────────── */
const AnimatedSVGAvatar = () => {
  const containerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    fetch('/avatar.svg')
      .then(r => r.text())
      .then(svgText => {
        if (cancelled || !containerRef.current) return;

        const parser = new DOMParser();
        const doc    = parser.parseFromString(svgText, 'image/svg+xml');
        const svg    = doc.querySelector('svg');

        svg.setAttribute('width',  '200');
        svg.setAttribute('height', '200');
        svg.removeAttribute('enable-background');

        // Stagger each path: 300 ms head-start, then 3 ms per path
        // VTracer outputs background→body→details, so order is naturally correct
        svg.querySelectorAll('path').forEach((path, i) => {
          path.style.opacity   = '0';
          path.style.animation = `avatar-paint 0.28s ease-out ${300 + i * 3}ms both`;
        });

        containerRef.current.innerHTML = '';
        containerRef.current.appendChild(svg);
      })
      .catch(() => { /* fail silently – orbit rings still show */ });

    return () => { cancelled = true; };
  }, []);

  return (
    <div
      ref={containerRef}
      className="rounded-full overflow-hidden shadow-2xl"
      style={{ width: 200, height: 200 }}
    />
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   AVATAR PANEL  –  orbit rings around the particle canvas
───────────────────────────────────────────────────────────────────────────── */
const AvatarPanel = () => (
  <div className="relative flex items-center justify-center" style={{ width: 320, height: 340 }}>

    {/* Orbit + badge SVG layer */}
    <svg
      viewBox="0 0 320 320"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden="true"
    >
      <g className="orbit-spin">
        <circle cx="160" cy="160" r="148" stroke="#cbd5e1" strokeWidth="1.1" strokeDasharray="5 10" />
      </g>
      <circle cx="160" cy="160" r="110" stroke="#d5e3f5" strokeWidth="1.5"
        className="d" style={{ '--d': 691, animationDelay: '300ms' }} />

      {/* Clipboard badge – top-left */}
      <g className="p fa" style={{ animationDelay: '2200ms' }}>
        <rect x="18" y="72" width="42" height="52" rx="8" fill="white" stroke="#d5e3f5" strokeWidth="1.5" />
        <rect x="29" y="66" width="20" height="9"  rx="3" fill="white" stroke="#adc7eb" strokeWidth="1.2" />
        <line x1="26" y1="88"  x2="52" y2="88"  stroke="#79a4de" strokeWidth="1.2" />
        <line x1="26" y1="97"  x2="52" y2="97"  stroke="#79a4de" strokeWidth="1.2" />
        <line x1="26" y1="106" x2="40" y2="106" stroke="#79a4de" strokeWidth="1.2" />
      </g>

      {/* Check badge – bottom-right */}
      <g className="p fb" style={{ animationDelay: '2350ms' }}>
        <circle cx="266" cy="250" r="26" fill="white" stroke="#ffafad" strokeWidth="1.5" />
        <path d="M 255 250 L 263 259 L 278 241"
          stroke="#fc4e48" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      </g>

      {/* Sparkle – top-right */}
      <g className="p fc" style={{ animationDelay: '2500ms' }}>
        <path d="M 278 68 L 281 77 L 290 80 L 281 83 L 278 92 L 275 83 L 266 80 L 275 77 Z"
          fill="#fc4e48" opacity="0.78" />
      </g>

      <circle cx="50"  cy="240" r="5"   fill="#fc4e48" opacity="0.28" className="p fb" style={{ animationDelay: '2600ms' }} />
      <circle cx="270" cy="72"  r="3.5" fill="#0d4993" opacity="0.28"  className="p fa" style={{ animationDelay: '2550ms' }} />
    </svg>

    {/* Particle canvas */}
    <div className="relative z-10">
      <ParticlePortrait />
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────────────────────────── */
const ResourcesDashboard = () => {
  const { user } = useAuth();
  const [manager, setManager] = useState(null);

  const firstName = user?.first_name || '';
  const greeting  = (() => {
    const h = new Date().getHours();
    if (h < 11) return 'Guten Morgen';
    if (h < 18) return 'Guten Tag';
    return 'Guten Abend';
  })();

  useEffect(() => {
    let cancelled = false;

    const loadManager = async () => {
      try {
        const { data } = await resourceAPI.getAccountManager();
        if (!cancelled && data?.assigned) {
          setManager(data.manager);
        }
      } catch {
        // Optionales Dashboard-Element; bei Fehlern bleibt es ausgeblendet.
      }
    };

    loadManager();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="flex-1 flex flex-col min-h-0 w-full">
      <style>{STYLES}</style>

      <div
        className="relative overflow-hidden flex-1 w-full min-h-0"
        style={{
          minHeight: 'calc(100dvh - 3.5rem)',
          background: 'linear-gradient(128deg, #f8fafc 0%, rgba(13, 73, 147, 0.07) 52%, rgba(252, 78, 72, 0.05) 100%)',
        }}
      >
        {/* Background blobs */}
        <div className="absolute pointer-events-none" style={{
          top: '-90px', right: '-90px', width: 430, height: 430, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(13, 73, 147, 0.24) 0%, transparent 68%)',
          animation: 'lu-blob-a 17s ease-in-out infinite',
        }} />
        <div className="absolute pointer-events-none" style={{
          bottom: '-70px', left: '-70px', width: 390, height: 390, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(252, 78, 72, 0.15) 0%, transparent 68%)',
          animation: 'lu-blob-b 21s ease-in-out infinite 2.5s',
        }} />

        {/* Dot grid */}
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: 'radial-gradient(circle, rgba(100,116,139,0.065) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }} />

        {/* Two-column layout */}
        <div
          className="relative z-10 grid grid-cols-1 md:grid-cols-2 items-center gap-6 py-16 px-12 sm:px-14 md:px-16 lg:px-20 xl:px-24"
          style={{ minHeight: 'inherit' }}
        >
          {/* Left: text reveal */}
          <div>
            <div className="lu-fade mb-8" style={{ animationDelay: '100ms' }}>
              <span
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 px-3.5 py-1.5 rounded-full border border-slate-200/80 tracking-wide"
                style={{ backgroundColor: 'rgba(255,255,255,0.72)', backdropFilter: 'blur(10px)' }}
              >
                <Sparkles className="h-3 w-3 text-hrf-coral" />
                HR Factory Portal
              </span>
            </div>

            <span className="lu-mask mb-1.5">
              <span className="lu-line text-lg font-medium text-slate-400 tracking-wide"
                style={{ animationDelay: '250ms' }}>
                {greeting}{firstName ? `, ${firstName}` : ''}.
              </span>
            </span>

            <span className="lu-mask">
              <span className="lu-line text-5xl font-bold text-slate-900 tracking-tight leading-none"
                style={{ animationDelay: '420ms' }}>
                Dein HR-Alltag,
              </span>
            </span>
            <span className="lu-mask mb-7 mt-0.5">
              <span className="lu-line text-5xl font-bold tracking-tight leading-none"
                style={{ animationDelay: '560ms', color: '#0d4993' }}>
                vereinfacht.
              </span>
            </span>

            <p className="lu-fade text-slate-500 text-[15px] leading-relaxed mb-10 max-w-[340px]"
              style={{ animationDelay: '780ms' }}>
              Rechner, Selbstchecks und KI-Tools – griffbereit für deinen HR-Alltag.
            </p>

            {manager ? (
              <div
                className="lu-fade mb-8 inline-flex items-center gap-3 rounded-xl border border-slate-200 bg-white/80 px-3.5 py-2.5"
                style={{ animationDelay: '860ms' }}
              >
                <div className="w-9 h-9 rounded-full bg-hrf-navy text-white flex items-center justify-center text-xs font-bold">
                  {manager.initials || 'AM'}
                </div>
                <div className="leading-tight">
                  <p className="text-xs uppercase tracking-wide text-slate-400">Dein Account Manager</p>
                  <p className="text-sm font-semibold text-slate-800">{manager.display_name}</p>
                </div>
              </div>
            ) : null}

            <p
              className="lu-fade text-slate-600 text-sm leading-relaxed max-w-[340px]"
              style={{ animationDelay: '950ms' }}
            >
              Wähle links in der Navigation ein Werkzeug.
            </p>
          </div>

          {/* Right: particle portrait */}
          <div
            className="lu-fade hidden md:flex items-center justify-center"
            style={{ animationDelay: '200ms' }}
          >
            <AvatarPanel />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResourcesDashboard;
